/**
 * Optimiza imágenes manteniendo la relación de aspecto
 * @param {File} file - Archivo de imagen original
 * @param {Object} options - Opciones de optimización
 * @returns {Promise<File>} - Archivo de imagen optimizado
 */
export const optimizeImage = async (file, options = {}) => {
  const {
    maxWidth = 800,
    maxHeight = 800,
    quality = 0.8,
    maintainAspectRatio = true
  } = options;

  return new Promise((resolve, reject) => {
    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      reject(new Error('El archivo no es una imagen'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          // Calcular nuevas dimensiones manteniendo la relación de aspecto
          let newWidth = img.width;
          let newHeight = img.height;

          if (maintainAspectRatio) {
            // Si la imagen es más grande que los límites, redimensionar proporcionalmente
            if (img.width > maxWidth || img.height > maxHeight) {
              const aspectRatio = img.width / img.height;

              if (img.width > img.height) {
                // Imagen horizontal
                newWidth = maxWidth;
                newHeight = maxWidth / aspectRatio;
                
                // Si la altura excede el límite, ajustar por altura
                if (newHeight > maxHeight) {
                  newHeight = maxHeight;
                  newWidth = maxHeight * aspectRatio;
                }
              } else {
                // Imagen vertical o cuadrada
                newHeight = maxHeight;
                newWidth = maxHeight * aspectRatio;
                
                // Si el ancho excede el límite, ajustar por ancho
                if (newWidth > maxWidth) {
                  newWidth = maxWidth;
                  newHeight = maxWidth / aspectRatio;
                }
              }
            }
          } else {
            // Ajustar sin mantener aspecto (no recomendado)
            newWidth = Math.min(img.width, maxWidth);
            newHeight = Math.min(img.height, maxHeight);
          }

          // Crear canvas para redimensionar
          const canvas = document.createElement('canvas');
          canvas.width = newWidth;
          canvas.height = newHeight;
          
          const ctx = canvas.getContext('2d');
          
          // Mejorar la calidad del redimensionamiento
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Dibujar imagen redimensionada
          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          // Convertir canvas a blob
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Error al procesar la imagen'));
                return;
              }

              // Crear nuevo archivo con el blob optimizado
              const optimizedFile = new File(
                [blob],
                file.name,
                {
                  type: file.type,
                  lastModified: Date.now()
                }
              );

              resolve(optimizedFile);
            },
            file.type,
            quality
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Error al cargar la imagen'));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Formatea el tamaño de archivo en formato legible
 * @param {number} bytes - Tamaño en bytes
 * @returns {string} - Tamaño formateado (ej: "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Valida el tipo y tamaño de imagen
 * @param {File} file - Archivo a validar
 * @param {Object} options - Opciones de validación
 * @returns {Object} - {valid: boolean, error: string}
 */
export const validateImage = (file, options = {}) => {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB por defecto
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  } = options;

  if (!file) {
    return { valid: false, error: 'No se seleccionó ningún archivo' };
  }

  if (!allowedTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: `Tipo de archivo no permitido. Use: ${allowedTypes.join(', ')}` 
    };
  }

  if (file.size > maxSize) {
    return { 
      valid: false, 
      error: `El archivo es muy grande. Máximo: ${formatFileSize(maxSize)}` 
    };
  }

  return { valid: true, error: null };
};
